/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type GetAudioContextOptions = AudioContextOptions & {
  id?: string;
};

const map: Map<string, AudioContext> = new Map();

export const audioContext: (
  options?: GetAudioContextOptions,
) => Promise<AudioContext> = (() => {
  const didInteract = new Promise((res) => {
    window.addEventListener("pointerdown", res, { once: true });
    window.addEventListener("keydown", res, { once: true });
  });

  return async (options?: GetAudioContextOptions) => {
    try {
      const a = new Audio();
      a.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      await a.play();
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    } catch (e) {
      await didInteract;
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    }
  };
})();

export const blobToJSON = (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const json = JSON.parse(reader.result as string);
        resolve(json);
      } else {
        reject("oops");
      }
    };
    reader.readAsText(blob);
  });

function cleanBase64String(base64: string): string {
  // Convert URL-safe base64 to standard base64
  let cleaned = base64
    .replace(/-/g, '+')  // Replace - with +
    .replace(/_/g, '/')  // Replace _ with /
    .replace(/[^A-Za-z0-9+/=]/g, ''); // Remove any other invalid characters
  
  // Ensure proper padding (base64 strings must be multiples of 4)
  return cleaned + '='.repeat((4 - cleaned.length % 4) % 4);
}

export function base64ToArrayBuffer(base64: string) {
  const cleanedBase64 = cleanBase64String(base64);
  
  try {
    var binaryString = atob(cleanedBase64);
    var bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (error) {
    console.error('Failed to decode base64 audio data:', error);
    console.error('Original base64 length:', base64.length);
    console.error('Cleaned base64 length:', cleanedBase64.length);
    console.error('First 100 chars:', base64.substring(0, 100));
    // Return empty buffer on error
    return new ArrayBuffer(0);
  }
}

/**
 * Current time as ISO 8601 **with the browser's UTC offset** — e.g.
 * `2026-07-15T21:46:34+09:00`.
 *
 * `Date.prototype.toISOString()` returns UTC (`…Z`), which the backend cannot
 * tell apart from the user's local time: a user in Tokyo asking for a trip
 * "today" at 21:00 was reported as 12:00, and got a plan for their lunchtime.
 * Sending the offset makes the wall clock unambiguous and saves the backend a
 * timezone lookup.
 */
export function localISOWithOffset(date: Date = new Date()): string {
  const pad = (n: number) => String(Math.trunc(Math.abs(n))).padStart(2, "0");
  // getTimezoneOffset() is minutes BEHIND UTC, so the sign is inverted.
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(offsetMinutes / 60)}:${pad(offsetMinutes % 60)}`
  );
}
